<!-- Popular: horizontal strip of image cards -->
<ng-container *ngIf="card === 'popular'">
  <!-- uiDragScroll: pull the row with the mouse, like the category chips -->
  <div class="ui-hscroll ui-dishes" uiDragScroll>
    <!-- read-only (dine-in): the whole card is the tap target, no + button -->
    <div
      class="ui-card ui-dish"
      *ngFor="let recipe of recipes; trackBy: trackRecipe"
      [class.is-soldout]="!recipe.RecipeStock && !readOnly"
      [class.is-tappable]="readOnly"
      [attr.role]="readOnly ? 'button' : null"
      [attr.tabindex]="readOnly ? 0 : null"
      (click)="readOnly && getRecipeModal(recipe)"
      (keydown.enter)="readOnly && getRecipeModal(recipe)"
    >
      <div
        class="ui-dish__img"
        *ngIf="recipe.RecipeImage || readOnly"
        [ngClass]="recipe.RecipeImage ? '' : 'ui-swatch ' + swatch(recipe)"
      >
        <img
          *ngIf="recipe.RecipeImage"
          [src]="recipe.RecipeImage"
          [alt]="recipe?.RecipeTitle"
          loading="lazy"
          decoding="async"
          class="cursor-pointer"
          (click)="!readOnly && getRecipeModal(recipe)"
        />
        <span class="ui-swatch__initial" *ngIf="!recipe.RecipeImage" aria-hidden="true">
          {{ initial(recipe) }}
        </span>
      </div>
      <div class="ui-dish__body">
        <div>
          <div class="ui-dish__title">{{ recipe?.RecipeTitle }}</div>
          <small class="ui-dish__cat">In {{ recipe?.CategoryTitle }}</small>
        </div>
        <div class="ui-dish__foot">
          <div class="ui-dish__price">
            <ng-container
              *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
            >
              <span class="ui-badge ui-badge--sm">
                <ng-container
                  *ngIf="recipe.RecipeDiscountType === 'Percentage'"
                >
                  {{ recipe.RecipeDiscountAmount / 100 | percent: "1.0-2" }}
                  OFF
                </ng-container>
                <ng-container *ngIf="recipe.RecipeDiscountType === 'Cash'">
                  {{ recipe.RecipeDiscountAmount | currency: CurrencySymbol }}
                  OFF
                </ng-container>
              </span>
              <small class="ui-dish__old" *ngIf="recipe.RecipeOldPrice">
                {{ RecipeOldPriceText || "Before" }}
                <s>{{ recipe.RecipeOldPrice | currency: CurrencySymbol }}</s>
              </small>
            </ng-container>
            <b *ngIf="recipe.RecipePrice">
              <span
                *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
                >{{ RecipeNewPriceText || "Now" }}</span
              >
              {{ recipe?.RecipePrice | currency: CurrencySymbol }}
            </b>
            <b *ngIf="recipe.DisplayAmountText && !recipe.RecipePrice">
              {{ recipe?.DisplayAmountText }}
            </b>
          </div>
          <button
            *ngIf="!readOnly && recipe.RecipeStock"
            type="button"
            class="ui-btn ui-btn--sm ui-btn--icon"
            (click)="getRecipeModal(recipe)"
            [attr.aria-label]="'Add ' + recipe?.RecipeTitle"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>
          </button>
          <span
            class="ui-tag ui-tag--danger ui-soldout"
            *ngIf="!readOnly && !recipe.RecipeStock"
          >
            Sold out
          </span>
          <span
            class="ui-dish__more"
            [class.ui-more--options]="hasOptions(recipe)"
            *ngIf="readOnly"
          >
            {{ hasOptions(recipe) ? "Options" : "Details" }}
            <i class="fa fa-chevron-right" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Main: one card, a row per dish -->
<ng-container *ngIf="card === 'main'">
  <div
    class="ui-card ui-dishlist"
    [class.is-readonly]="readOnly"
    *ngIf="recipes?.length"
  >
    <div
      class="ui-dishrow"
      *ngFor="let recipe of recipes; trackBy: trackRecipe"
      [class.is-soldout]="!recipe.RecipeStock && !readOnly"
      [class.is-added]="recipe.isAddedToCart && isPromotedItem"
      [class.is-compact]="compact"
      [class.is-tappable]="readOnly"
      [attr.role]="readOnly ? 'button' : null"
      [attr.tabindex]="readOnly ? 0 : null"
      (click)="readOnly && getRecipeModal(recipe)"
      (keydown.enter)="readOnly && getRecipeModal(recipe)"
    >
      <!-- desktop only (see .ui-dishrow__img): the dish photo, or the
           coloured-initial swatch when there isn't one, so rows line up -->
      <div
        class="ui-dishrow__img"
        [ngClass]="recipe.RecipeImage ? '' : 'ui-swatch ' + swatch(recipe)"
      >
        <img
          *ngIf="recipe.RecipeImage"
          [src]="recipe.RecipeImage"
          [alt]="recipe?.RecipeTitle"
          loading="lazy"
        />
        <span class="ui-swatch__initial" *ngIf="!recipe.RecipeImage" aria-hidden="true">
          {{ initial(recipe) }}
        </span>
      </div>
      <div class="ui-dishrow__main">
        <div class="ui-dishrow__title">
          {{ recipe?.RecipeTitle }}
          <span
            class="ui-badge ui-badge--sm ui-dishrow__free"
            *ngIf="isPromotedItem"
            >Free</span
          >
        </div>
        <p
          class="ui-dishrow__desc"
          *ngIf="recipe.RecipeDescription"
          [class.is-clamped]="!readOnly && isLongDescription(recipe) && !isDescExpanded(recipe)"
          [class.is-oneline]="compact"
        >
          {{ recipe?.RecipeDescription }}
        </p>
        <button
          type="button"
          class="ui-link ui-dishrow__more-desc"
          *ngIf="!readOnly && !compact && isLongDescription(recipe)"
          (click)="toggleDesc(recipe, $event)"
          [attr.aria-expanded]="isDescExpanded(recipe)"
        >
          {{ isDescExpanded(recipe) ? "Show less" : "Read more" }}
        </button>
        <div class="ui-dishrow__allergens" *ngIf="recipe.Allergens?.length">
          <img
            *ngFor="let allergen of recipe.Allergens"
            [src]="allergen"
            alt=""
            width="26"
            height="26"
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
      <div class="ui-dishrow__side">
        <div
          class="ui-dishrow__discount"
          *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
        >
          <span class="ui-badge ui-badge--sm">
            <ng-container *ngIf="recipe.RecipeDiscountType === 'Percentage'">
              {{ recipe.RecipeDiscountAmount / 100 | percent: "1.0-2" }}
              OFF
            </ng-container>
            <ng-container *ngIf="recipe.RecipeDiscountType === 'Cash'">
              {{ recipe.RecipeDiscountAmount | currency: CurrencySymbol }}
              OFF
            </ng-container>
          </span>
          <small class="ui-dish__old" *ngIf="recipe.RecipeOldPrice">
            {{ RecipeOldPriceText || "Before" }}
            <s>{{ recipe.RecipeOldPrice | currency: CurrencySymbol }}</s>
          </small>
        </div>

        <!--
          A promotion ("free dish") row prices at zero with the paid price struck
          through beneath it, matching cards/recipe (the legacy popup card). Only
          this `main` variant is ever rendered with isPromotedItem true — both
          recommendations popups use it; `popular` and `recommended` are menu-only
          and always paid.
        -->
        <ng-container *ngIf="isPromotedItem; else paidPrice">
          <b class="ui-dishrow__price ui-dishrow__price--free">
            {{ 0 | currency: CurrencySymbol }}
          </b>
          <small class="ui-dish__old" *ngIf="recipe.RecipePrice">
            <s>{{ recipe?.RecipePrice | currency: CurrencySymbol }}</s>
          </small>
        </ng-container>

        <ng-template #paidPrice>
          <b class="ui-dishrow__price" *ngIf="recipe.RecipePrice">
            <span
              *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
              >{{ RecipeNewPriceText || "Now" }}</span
            >
            {{ recipe?.RecipePrice | currency: CurrencySymbol }}
          </b>

          <b
            class="ui-dishrow__price"
            *ngIf="recipe.DisplayAmountText && !recipe.RecipePrice"
          >
            {{ recipe?.DisplayAmountText }}
          </b>
        </ng-template>

        <button
          *ngIf="recipe.RecipeStock && addTocart && !recipe.isAddedToCart"
          type="button"
          class="ui-btn ui-btn--sm ui-dishrow__add"
          [class.ui-btn--icon]="compact"
          [attr.aria-label]="compact ? 'Add ' + recipe?.RecipeTitle : null"
          (click)="getRecipeModal(recipe)"
        >
          <!-- two <i>s, not one with a bound class: Font Awesome swaps each
               <i> for an <svg> once, so a later class change wouldn't show -->
          <i *ngIf="compact" class="fa fa-plus" aria-hidden="true"></i>
          <ng-container *ngIf="!compact">
            <i class="fa fa-shopping-basket" aria-hidden="true"></i>
            Add
          </ng-container>
        </button>

        <!-- out of stock: say so where the button would be -->
        <span
          class="ui-tag ui-tag--danger ui-soldout"
          *ngIf="!recipe.RecipeStock && addTocart && !readOnly"
        >
          Sold out
        </span>

        <span
          *ngIf="recipe.isAddedToCart && isPromotedItem"
          class="ui-badge ui-dishrow__added"
        >
          <i class="fa fa-check" aria-hidden="true"></i> Added
        </span>

        <!-- read-only: the row itself opens the modal; say when there is
             something to choose in there -->
        <span
          class="ui-dishrow__more"
          [class.ui-more--options]="hasOptions(recipe)"
          *ngIf="readOnly"
        >
          {{ hasOptions(recipe) ? "Options" : "Details" }}
          <i class="fa fa-chevron-right" aria-hidden="true"></i>
        </span>
      </div>
    </div>
  </div>
</ng-container>

<!-- Recommended: compact rows with a thumbnail (lives inside the host's card) -->
<ng-container *ngIf="card === 'recommended'">
  <div class="ui-reclist">
    <div
      class="ui-rec"
      *ngFor="let recipe of recipes; trackBy: trackRecipe"
      [class.is-soldout]="!recipe.RecipeStock && !readOnly"
      [class.is-tappable]="readOnly"
      [attr.role]="readOnly ? 'button' : null"
      [attr.tabindex]="readOnly ? 0 : null"
      (click)="readOnly && getRecipeModal(recipe)"
      (keydown.enter)="readOnly && getRecipeModal(recipe)"
    >
      <div class="ui-thumb ui-rec__img" *ngIf="recipe.RecipeImage">
        <img
          [src]="recipe.RecipeImage"
          [alt]="recipe?.RecipeTitle"
          loading="lazy"
          decoding="async"
          class="cursor-pointer"
          (click)="!readOnly && getRecipeModal(recipe)"
        />
      </div>
      <div class="ui-rec__body">
        <div class="ui-rec__title">{{ recipe?.RecipeTitle }}</div>
        <div class="ui-rec__foot">
          <div class="ui-dish__price">
            <ng-container
              *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
            >
              <span class="ui-badge ui-badge--sm">
                <ng-container
                  *ngIf="recipe.RecipeDiscountType === 'Percentage'"
                >
                  {{ recipe.RecipeDiscountAmount / 100 | percent: "1.0-2" }}
                  OFF
                </ng-container>
                <ng-container *ngIf="recipe.RecipeDiscountType === 'Cash'">
                  {{ recipe.RecipeDiscountAmount | currency: CurrencySymbol }}
                  OFF
                </ng-container>
              </span>
              <small class="ui-dish__old" *ngIf="recipe.RecipeOldPrice">
                {{ RecipeOldPriceText || "Before" }}
                <s>{{ recipe.RecipeOldPrice | currency: CurrencySymbol }}</s>
              </small>
            </ng-container>
            <b *ngIf="recipe.RecipePrice">
              <span
                *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
                >{{ RecipeNewPriceText || "Now" }}</span
              >
              {{ recipe?.RecipePrice | currency: CurrencySymbol }}
            </b>
            <b *ngIf="recipe.DisplayAmountText && !recipe.RecipePrice">
              {{ recipe?.DisplayAmountText }}
            </b>
          </div>
          <button
            *ngIf="!readOnly && recipe.RecipeStock"
            type="button"
            class="ui-btn ui-btn--sm ui-btn--icon"
            (click)="getRecipeModal(recipe)"
            [attr.aria-label]="'Add ' + recipe?.RecipeTitle"
          >
            <i class="fa fa-plus" aria-hidden="true"></i>
          </button>
          <span
            class="ui-tag ui-tag--danger ui-soldout"
            *ngIf="!readOnly && !recipe.RecipeStock"
          >
            Sold out
          </span>
          <span
            class="ui-dish__more"
            [class.ui-more--options]="hasOptions(recipe)"
            *ngIf="readOnly"
          >
            <i class="fa fa-chevron-right" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </div>
  </div>
</ng-container>

<!-- Tile: photo grid (the dine-in photo board). The first dish of a group
     is doubled in width so every section opens with a big picture. -->
<ng-container *ngIf="card === 'tile'">
  <div class="ui-tiles" *ngIf="recipes?.length">
    <div
      class="ui-card ui-tile"
      *ngFor="let recipe of recipes; let i = index; trackBy: trackRecipe"
      [class.is-soldout]="!recipe.RecipeStock && !readOnly"
      [class.ui-tile--wide]="i === 0 && recipes.length > 2"
      [class.ui-tile--noimg]="!recipe.RecipeImage"
      [class.is-tappable]="readOnly"
      [attr.role]="readOnly ? 'button' : null"
      [attr.tabindex]="readOnly ? 0 : null"
      (click)="readOnly && getRecipeModal(recipe)"
      (keydown.enter)="readOnly && getRecipeModal(recipe)"
    >
      <div
        class="ui-tile__img"
        [ngClass]="recipe.RecipeImage ? '' : 'ui-swatch ' + swatch(recipe)"
      >
        <img
          *ngIf="recipe.RecipeImage"
          [src]="recipe.RecipeImage"
          [alt]="recipe?.RecipeTitle"
          loading="lazy"
        />
        <span class="ui-swatch__initial" *ngIf="!recipe.RecipeImage" aria-hidden="true">
          {{ initial(recipe) }}
        </span>
      </div>
      <div class="ui-tile__body">
        <div class="ui-tile__title">{{ recipe?.RecipeTitle }}</div>
        <p class="ui-tile__desc" *ngIf="recipe.RecipeDescription">
          {{ recipe?.RecipeDescription }}
        </p>
        <div class="ui-dishrow__allergens" *ngIf="recipe.Allergens?.length">
          <img
            *ngFor="let allergen of recipe.Allergens"
            [src]="allergen"
            alt=""
            width="26"
            height="26"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div class="ui-tile__foot">
          <div class="ui-tile__meta">
            <ng-container
              *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
            >
              <span class="ui-badge ui-badge--sm">
                <ng-container
                  *ngIf="recipe.RecipeDiscountType === 'Percentage'"
                >
                  {{ recipe.RecipeDiscountAmount / 100 | percent: "1.0-2" }}
                  OFF
                </ng-container>
                <ng-container *ngIf="recipe.RecipeDiscountType === 'Cash'">
                  {{ recipe.RecipeDiscountAmount | currency: CurrencySymbol }}
                  OFF
                </ng-container>
              </span>
              <small class="ui-dish__old" *ngIf="recipe.RecipeOldPrice">
                {{ RecipeOldPriceText || "Before" }}
                <s>{{ recipe.RecipeOldPrice | currency: CurrencySymbol }}</s>
              </small>
            </ng-container>
            <span
              class="ui-dish__more"
              [class.ui-more--options]="hasOptions(recipe)"
              *ngIf="readOnly"
            >
              {{ hasOptions(recipe) ? "Options" : "Details" }}
              <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </span>
          </div>
          <div class="ui-tile__price">
            <b *ngIf="recipe.RecipePrice">
              <span
                *ngIf="recipe.RecipeDiscountType && recipe.RecipeDiscountAmount"
                >{{ RecipeNewPriceText || "Now" }}</span
              >
              {{ recipe?.RecipePrice | currency: CurrencySymbol }}
            </b>
            <b *ngIf="recipe.DisplayAmountText && !recipe.RecipePrice">
              {{ recipe?.DisplayAmountText }}
            </b>
            <button
              *ngIf="!readOnly && recipe.RecipeStock"
              type="button"
              class="ui-btn ui-btn--sm ui-btn--icon"
              (click)="getRecipeModal(recipe)"
              [attr.aria-label]="'Add ' + recipe?.RecipeTitle"
            >
              <i class="fa fa-plus" aria-hidden="true"></i>
            </button>
            <span
              class="ui-tag ui-tag--danger ui-soldout"
              *ngIf="!readOnly && !recipe.RecipeStock"
            >
              Sold out
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-container>
